// src/App.js — public quiz app (single file)
import React, { useEffect, useState } from "react";
import localConfig from "./imported-personality-quiz-website-four.json"; // bundled fallback JSON

/** 1) Point this at your public JSON URL */
const CONFIG_URL =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/Interactive%20Content/imported-personality-quiz-website-four.json";

/** 2) Global tokens: font + background */
const Tokens = () => (
  <style>{`
    :root{
      --ink:#0B1A3C;
      --muted:#667085;
      --line:#EAECF0;
      --bg:#F7F8FA;
      --card:#FFFFFF;
      --brand:#17408B;
      --brandSoft:#E3ECFF;
      --resultBg:#F1F8E9;
      --btn:#1D4ED8;
    }
    html, body, #root { height: 100%; margin: 0; padding: 0; }
    body{
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      background: var(--bg);
      color: var(--ink);
    }
    button { font-family: inherit; }
  `}</style>
);

/** 3) Main app */
export default function App() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentIdx, setCurrentIdx] = useState(0); // 0..questions.length, last = result
  const [answers, setAnswers] = useState({}); // { [qIdx]: outcomeKey }

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");

        // Try remote R2 JSON first
        const res = await fetch(CONFIG_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setConfig(json);
      } catch (e) {
        console.error("Remote config failed, falling back to local JSON", e);
        // Fallback to bundled JSON
        setConfig(localConfig);
        setError("");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg)",
        }}
      >
        <Tokens />
        <p style={{ fontSize: 14, color: "var(--muted)" }}>
          Loading your GTM personality quiz…
        </p>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--bg)",
        }}
      >
        <Tokens />
        <p style={{ fontSize: 14, color: "#B91C1C" }}>
          {error || "No quiz config found."}
        </p>
      </div>
    );
  }

  // Assume config is the project we exported from the builder
  const firstStep = (config.steps || [])[0] || {};
  const data = firstStep.data || {};
  const questions = data.questions || [];
  const outcomesArr = data.outcomes || [];

  const outcomesByKey = {};
  outcomesArr.forEach((o) => {
    if (!o?.key) return;
    outcomesByKey[o.key] = o;
  });

  const isResultTab = currentIdx >= questions.length;

  const handleAnswer = (qIdx, outcomeKey) => {
    if (!outcomeKey) return;
    setAnswers((prev) => {
      const next = { ...prev, [qIdx]: outcomeKey };
      // Auto-advance
      if (qIdx < questions.length - 1) {
        setCurrentIdx(qIdx + 1);
      } else {
        setCurrentIdx(questions.length); // result tab
      }
      return next;
    });
  };

  // Compute winner
  let winningKey = null;
  if (Object.keys(answers).length) {
    const tally = {};
    Object.values(answers).forEach((k) => {
      if (!k) return;
      tally[k] = (tally[k] || 0) + 1;
    });
    const sorted = Object.entries(tally).sort((a, b) => b[1] - a[1]);
    winningKey = sorted[0]?.[0] || null;
  }
  const winningOutcome = winningKey ? outcomesByKey[winningKey] : null;

  const heroImageUrl = data.heroImageUrl || "";
  const logoImageUrl = data.logoImageUrl || "";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        padding: "24px 16px",
        boxSizing: "border-box",
      }}
    >
      <Tokens />
      <main
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          background: "var(--card)",
          borderRadius: 24,
          boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
          border: "1px solid var(--line)",
          overflow: "hidden",
        }}
      >
        {/* Top title */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            {data.title || config.name || "Imported Quiz"}
          </h1>
          {data.subtitle && (
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                color: "var(--muted)",
              }}
            >
              {data.subtitle}
            </p>
          )}
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "10px 16px",
            background: "var(--brand)",
            color: "white",
            overflowX: "auto",
          }}
        >
          {questions.map((_, idx) => {
            const active = currentIdx === idx && !isResultTab;
            return (
              <button
                key={idx}
                onClick={() => setCurrentIdx(idx)}
                style={{
                  border: "none",
                  background: active ? "white" : "transparent",
                  color: active ? "var(--brand)" : "white",
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  marginRight: 8,
                  whiteSpace: "nowrap",
                }}
              >
                #{idx + 1}
              </button>
            );
          })}
          <button
            onClick={() => setCurrentIdx(questions.length)}
            style={{
              marginLeft: "auto",
              border: "none",
              background: isResultTab ? "white" : "#1E40AF",
              color: isResultTab ? "var(--brand)" : "white",
              padding: "6px 16px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            RESULT
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Hero row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 1.5fr)", // a bit less dominant
              gap: 16,
              alignItems: "center",
            }}
          >
            {/* Left: hero image (smaller + capped height) */}
            <div
              style={{
                minHeight: 140,
                maxHeight: 200,
                background: "var(--brandSoft)",
                borderRadius: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                color: "rgba(15,23,42,0.7)",
                overflow: "hidden",
                padding: 8,
              }}
            >
              {heroImageUrl ? (
                <img
                  src={heroImageUrl}
                  alt="Quiz hero"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    background: "white",
                  }}
                />
              ) : (
                "Hero image placeholder"
              )}
            </div>

            {/* Right: logo */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                padding: "8px 4px",
              }}
            >
              {logoImageUrl && (
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(15,23,42,0.55)",
                      marginBottom: 6,
                      textTransform: "uppercase",
                      letterSpacing: 0.08,
                      fontWeight: 600,
                    }}
                  >
                    Powered by
                  </div>
                  <img
                    src={logoImageUrl}
                    alt="Logo"
                    style={{
                      maxHeight: 36,
                      maxWidth: 140,
                      objectFit: "contain",
                      display: "inline-block",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Question or Result */}
          {!isResultTab ? (
            <QuestionCard
              qIdx={currentIdx}
              question={questions[currentIdx]}
              selectedOutcome={answers[currentIdx]}
              onSelect={handleAnswer}
            />
          ) : (
            <ResultCard winningOutcome={winningOutcome} />
          )}
        </div>
      </main>
    </div>
  );
}

/** 4) Question card */
function QuestionCard({ qIdx, question, selectedOutcome, onSelect }) {
  if (!question) return null;

  const options = [];
  if (question.a?.label) options.push({ key: "a", ...question.a });
  if (question.b?.label) options.push({ key: "b", ...question.b });
  if (question.c?.label) options.push({ key: "c", ...question.c });
  if (question.d?.label) options.push({ key: "d", ...question.d });

  // Support either `imageUrl` or `questionImageUrl` from the builder JSON
  const questionImage = question.imageUrl || question.questionImageUrl || null;

  return (
    <div
      style={{
        borderRadius: 20,
        border: "1px solid var(--line)",
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          color: "var(--muted)",
          marginBottom: 4,
        }}
      >
        Q{qIdx + 1}
      </div>

      {/* Question image block with locked aspect ratio */}
      {questionImage && (
        <div
          style={{
            width: "100%",
            borderRadius: 24,
            border: "1px solid var(--line)",
            background: "#F9FAFB",
            marginBottom: 16,
            overflow: "hidden",
            aspectRatio: "16 / 9",
            maxHeight: 360,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={questionImage}
            alt={question.prompt || "Question visual"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </div>
      )}

      <h2
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        {question.prompt || "Question"}
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
          gap: 10,
        }}
      >
        {options.map((opt) => {
          const isActive = selectedOutcome === opt.to;
          return (
            <button
              key={opt.key}
              onClick={() => onSelect(qIdx, opt.to)}
              style={{
                borderRadius: 999,
                padding: "10px 16px",
                fontSize: 14,
                border: isActive
                  ? "2px solid var(--btn)"
                  : "1px solid var(--line)",
                background: isActive ? "var(--brandSoft)" : "white",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {opt.label || "Option"}
            </button>
          );
        })}
      </div>

      <p
        style={{
          marginTop: 10,
          fontSize: 11,
          color: "var(--muted)",
        }}
      >
        Tap an option to move to the next question.
      </p>
    </div>
  );
}

/** 5) Result card */
function ResultCard({ winningOutcome }) {
  if (!winningOutcome) {
    return (
      <div
        style={{
          borderRadius: 20,
          background: "var(--resultBg)",
          padding: 20,
          border: "1px solid #D9E6C3",
          fontSize: 13,
          color: "#374151",
        }}
      >
        Answer a few questions to see your GTM personality result.
      </div>
    );
  }

  const {
    title,
    subtitle,
    summary,
    problem,
    opportunity,
    recommendedPlays,
    metricsToWatch,
    cta,
    href,
    imageUrl, // 👈 NEW: outcome visual from builder JSON
  } = winningOutcome;

  return (
    <div
      style={{
        borderRadius: 20,
        background: "var(--resultBg)",
        padding: 20,
        border: "1px solid #D9E6C3",
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          color: "#4A6B2F",
          marginBottom: 4,
        }}
      >
        Your GTM Personality
      </div>

      {/* Result image block */}
      {imageUrl && (
        <div
          style={{
            width: "100%",
            borderRadius: 20,
            border: "1px solid rgba(74,107,47,0.25)",
            background: "#ECFDF3",
            margin: "8px 0 14px",
            overflow: "hidden",
            aspectRatio: "16 / 9",
            maxHeight: 320,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={imageUrl}
            alt={title || "GTM personality visual"}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </div>
      )}

      <h2
        style={{
          margin: 0,
          fontSize: 22,
          fontWeight: 700,
          color: "#14532D",
          marginBottom: 4,
        }}
      >
        {title || "Demand Gen Profile"}
      </h2>

      {subtitle && (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 500,
            color: "#166534",
            marginBottom: 8,
          }}
        >
          {subtitle}
        </p>
      )}

      <p
        style={{
          fontSize: 14,
          color: "#374151",
          maxWidth: 640,
          marginBottom: 12,
        }}
      >
        {summary ||
          "Based on your answers, this is the best starting point for working with Fairway Digital Media."}
      </p>

      {problem && (
        <>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginTop: 12,
              marginBottom: 4,
              color: "#14532D",
            }}
          >
            What this usually means
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#374151",
              maxWidth: 640,
              margin: 0,
            }}
          >
            {problem}
          </p>
        </>
      )}

      {opportunity && (
        <>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginTop: 12,
              marginBottom: 4,
              color: "#14532D",
            }}
          >
            Where to focus first
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#374151",
              maxWidth: 640,
              margin: 0,
            }}
          >
            {opportunity}
          </p>
        </>
      )}

      {Array.isArray(recommendedPlays) && recommendedPlays.length > 0 && (
        <>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginTop: 12,
              marginBottom: 4,
              color: "#14532D",
            }}
          >
            Recommended plays
          </h3>
          <ul
            style={{
              fontSize: 13,
              color: "#374151",
              paddingLeft: 20,
              marginTop: 0,
              maxWidth: 640,
            }}
          >
            {recommendedPlays.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </>
      )}

      {Array.isArray(metricsToWatch) && metricsToWatch.length > 0 && (
        <>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginTop: 12,
              marginBottom: 4,
              color: "#14532D",
            }}
          >
            Metrics to watch
          </h3>
          <ul
            style={{
              fontSize: 13,
              color: "#374151",
              paddingLeft: 20,
              marginTop: 0,
              maxWidth: 640,
            }}
          >
            {metricsToWatch.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </>
      )}

      <button
        style={{
          border: "none",
          background: "var(--btn)",
          color: "white",
          borderRadius: 999,
          padding: "10px 24px",
          fontSize: 14,
          fontWeight: 600,
          cursor: "pointer",
          marginTop: 16,
        }}
        onClick={() => {
          const url = href || "#";
          if (url && url !== "#") window.open(url, "_blank");
        }}
      >
        {cta || "Learn more"}
      </button>
    </div>
  );
}
